import { useState } from "react";
import { useCurrentLang } from "@/i18n/language";
import type { NatureContent } from "@/pages/bali-info-detail/data/types";
import NaturePhoto from "@/pages/bali-info-detail/components/NaturePhoto";

const groupIcons = [
  "ri-plant-line",
  "ri-seedling-line",
  "ri-tree-line",
  "ri-flower-line",
  "ri-leaf-line",
  "ri-flower-line",
  "ri-water-flash-line",
  "ri-link",
];

type ActivePhoto = { src: string; alt: string } | null;

export default function NatureArticle({ content }: { content: NatureContent }) {
  const lang = useCurrentLang();
  const [activePhoto, setActivePhoto] = useState<ActivePhoto>(null);

  const scrollToGroup = (index: number) => {
    const el = document.getElementById(`nature-group-${index}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div>
      <div className="rounded-lg border border-accent-200 bg-accent-50/70 p-5 md:p-6 mb-8">
        <p className="text-sm text-foreground-700 leading-relaxed">
          {content.intro[lang]}
        </p>
        {content.namingHint ? (
          <p className="text-xs text-foreground-600 leading-relaxed mt-3 pt-3 border-t border-accent-200/70">
            <i className="ri-information-line mr-1.5"></i>
            {content.namingHint[lang]}
          </p>
        ) : null}
      </div>

      {content.groups.length > 2 ? (
        <div className="flex flex-wrap gap-2 mb-10">
          {content.groups.map((group, index) => (
            <button
              type="button"
              key={`jump-${group.title.de}`}
              onClick={() => scrollToGroup(index)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-background-200 bg-background-50 text-xs text-foreground-700 hover:border-accent-500 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-down-line"></i>
              {group.title[lang]}
            </button>
          ))}
        </div>
      ) : null}

      <div className="space-y-12">
        {content.groups.map((group, groupIndex) => (
          <section
            key={group.title.de}
            id={`nature-group-${groupIndex}`}
            className="scroll-mt-28"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-900 flex-shrink-0">
                <i
                  className={`${groupIcons[groupIndex % groupIcons.length]} text-base`}
                ></i>
              </div>
              <div>
                <h3 className="font-heading text-xl text-foreground-950 leading-snug">
                  {group.title[lang]}
                </h3>
                {group.text ? (
                  <p className="text-sm text-foreground-600 leading-relaxed mt-1">
                    {group.text[lang]}
                  </p>
                ) : null}
              </div>
            </div>

            {group.image ? (
              <div className="mt-4 mb-4 w-full h-44 md:h-60 overflow-hidden rounded-lg bg-background-200">
                <img
                  src={group.image}
                  alt={group.imageAlt ? group.imageAlt[lang] : group.title[lang]}
                  className="w-full h-full object-cover object-center"
                />
              </div>
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mt-4">
              {group.entries.map((entry) => (
                <div
                  key={entry.name.de}
                  className="bg-background-100 border border-background-200 rounded-lg p-4 md:p-5"
                >
                  <h4 className="font-heading text-base text-foreground-950">
                    {entry.name[lang]}
                  </h4>
                  {entry.local ? (
                    <p className="text-xs text-foreground-500 mt-0.5">
                      {entry.local}
                    </p>
                  ) : null}
                  {entry.latin ? (
                    <p className="text-xs text-accent-700 italic mt-0.5 mb-2">
                      {entry.latin}
                    </p>
                  ) : null}
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    {entry.text[lang]}
                  </p>

                  {entry.photos && entry.photos.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2 mt-4">
                      {entry.photos.map((src) => (
                        <NaturePhoto
                          key={src}
                          src={src}
                          alt={entry.name[lang]}
                          onOpen={(photoSrc, photoAlt) =>
                            setActivePhoto({ src: photoSrc, alt: photoAlt })
                          }
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      {content.closing ? (
        <div className="mt-10 pl-4 border-l-2 border-accent-500">
          <p className="text-sm text-foreground-700 leading-relaxed">
            {content.closing[lang]}
          </p>
        </div>
      ) : null}

      {activePhoto ? (
        <div
          className="fixed inset-0 z-[60] bg-foreground-950/85 flex items-center justify-center p-4 md:p-10"
          onClick={() => setActivePhoto(null)}
          role="presentation"
        >
          <div className="relative max-w-4xl w-full">
            <img
              src={activePhoto.src}
              alt={activePhoto.alt}
              className="w-full max-h-[80vh] object-contain rounded-lg"
            />
            <p className="text-xs text-background-50/80 text-center mt-3">
              {activePhoto.alt}
            </p>
            <button
              type="button"
              onClick={() => setActivePhoto(null)}
              aria-label="Schließen"
              className="absolute -top-3 -right-3 w-10 h-10 flex items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-700 cursor-pointer shadow-lg transition-colors"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}